import { useEffect, useMemo } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import ChartShell from '@/components/Chart/ChartShell';
import type { ExtendedOptions } from '@/components/Chart/types/highcharts';
import Loader from '@/components/Loader';
import { DEFAULT_CHART_HEIGHT, regularChartPalette } from '@/constants/constants';
import { chartOptions } from '@/features/Charts/api';
import { deepMerge } from '@/utils/deepMerge';
import { detectEnvironment } from '@/utils/detectEnvironment';

import { chartSeriesHasData } from './chartSeriesHasData';

import type Highcharts from 'highcharts/highstock';

const environment = detectEnvironment();
const GROUP_NAME = `Meta_Go_Config${environment === 'staging' ? '_Staging' : ''}`;

const resolveSankeyTooltipFormatter = (tooltip?: Highcharts.TooltipOptions) => {
  if (!tooltip?.formatter) return;
  if (typeof tooltip.formatter === 'function') return;

  const rawFormatter = tooltip.formatter as unknown;
  if (typeof rawFormatter === 'string') {
    const formatterKey = rawFormatter.trim();
    if (formatterKey && !formatterKey.includes('function') && !formatterKey.includes('=>')) {
      tooltip.pointFormat = tooltip.pointFormat ?? formatterKey;
      delete tooltip.formatter;
      return;
    }
  }

  delete tooltip.formatter;
};

const PreviewCustomChart = ({
  metadata,
  from,
  onHasDataChange,
  onChartReady,
  to
}: {
  metadata: Record<string, object | string>;
  from?: string;
  onHasDataChange?: (hasData: boolean | null) => void;
  onChartReady?: (chart: Highcharts.Chart | null) => void;
  to?: string;
}) => {
  const theme = useTheme();

  const configFileName = metadata.configFileName as string | undefined;
  const seriesFileName = metadata.seriesFileName as string | undefined;
  const queryFileId = metadata.queryFileId as string | undefined;

  const { data: chartConfig, isLoading: isConfigLoading } = useQuery(
    chartOptions.customConfig(configFileName, GROUP_NAME)
  );
  const { data: chartSeries, isLoading: isSeriesLoading } = useQuery(
    chartOptions.customData({
      fileName: seriesFileName,
      groupName: GROUP_NAME,
      queryFileId,
      from,
      to
    })
  );

  const resolvedOptions = useMemo<ExtendedOptions | null>(() => {
    if (!chartConfig || !chartSeries) return null;

    const baseOptions: ExtendedOptions = {
      accessibility: { enabled: false },
      title: { text: undefined },
      colors: regularChartPalette,
      exporting: {
        enabled: true,
        buttons: { contextButton: { enabled: false } }
      },
      tooltip: { enabled: false },
      stockTools: { gui: { enabled: false } },
      legend: { enabled: true, itemStyle: { color: theme.palette.text.primary } },
      credits: { enabled: false },
      navigator: { enabled: false },
      rangeSelector: { enabled: false },
      scrollbar: { enabled: false },
      xAxis: { labels: { style: { color: theme.palette.text.primary } } },
      yAxis: {
        title: { text: '' },
        labels: { style: { color: theme.palette.text.primary } }
      },
      series: chartSeries,
      chart: {
        reflow: true,
        animation: false,
        backgroundColor: theme.palette.background.paper,
        height: DEFAULT_CHART_HEIGHT,
        style: { color: theme.palette.text.primary, fontFamily: 'Cairo, sans-serif' }
      }
    };

    // structuredClone to unfreeze React Query cached objects before Highcharts mutates them
    const merged = deepMerge(structuredClone(baseOptions), structuredClone(chartConfig));

    if (merged.chart?.type === 'sankey') {
      resolveSankeyTooltipFormatter(merged.tooltip);
    }

    return merged;
  }, [chartConfig, chartSeries, theme]);

  const hasRenderableData = chartSeriesHasData(chartSeries) && Boolean(resolvedOptions);

  useEffect(() => {
    if (!onHasDataChange) return;

    if (isConfigLoading || isSeriesLoading) {
      onHasDataChange(null);
      return;
    }

    onHasDataChange(hasRenderableData);
  }, [hasRenderableData, isConfigLoading, isSeriesLoading, onHasDataChange]);

  useEffect(() => {
    if (!onChartReady) return;

    if (isConfigLoading || isSeriesLoading || !hasRenderableData) {
      onChartReady(null);
    }
  }, [hasRenderableData, isConfigLoading, isSeriesLoading, onChartReady]);

  // Separate cleanup so Strict Mode double-invocation doesn't reset chartInstance
  // to null and then leave it null (the body above won't re-set it when data is ready,
  // only ChartShell.onChartReady does that on its own mount).
  useEffect(() => {
    return () => {
      onChartReady?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isConfigLoading || isSeriesLoading) {
    return (
      <Box sx={{ height: DEFAULT_CHART_HEIGHT }}>
        <Loader />
      </Box>
    );
  }

  if (!hasRenderableData) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: DEFAULT_CHART_HEIGHT,
          color: 'text.secondary'
        }}
      >
        <Typography variant='body2'>No data available for selected time period</Typography>
      </Box>
    );
  }

  return (
    <ChartShell
      isStockChart={false}
      options={resolvedOptions ?? undefined}
      onChartReady={chart => onChartReady?.(chart)}
    />
  );
};

export default PreviewCustomChart;
